import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import "../css/Reports.css";
import * as XLSX from "xlsx";

export default function SupplierReport(props) {
  const params = useParams();
  const supplierId = props.supplierId || params.supplierId;
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10); // can be 10, 20, 50, or 'all'
  const [rawData, setRawData] = useState([]); // holds all API data
  const [supplierName, setSupplierName] = useState(""); // NEW: supplier name state
  const [invoiceSearch, setInvoiceSearch] = useState(""); // NEW: search field state

  // Fetch supplier name from supplier-list.php
  useEffect(() => {
    if (!supplierId) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}supplier-list.php`)
      .then((res) => {
        const suppliers = res.data.suppliers || [];
        const found = suppliers.find(
          (s) => String(s.id) === String(supplierId)
        );
        setSupplierName(found ? found.suppliername : "");
      })
      .catch(() => setSupplierName(""));
  }, [supplierId]);

  useEffect(() => {
    setLoading(true);
    setError("");
    axios
      .post(
        `${process.env.REACT_APP_API_URL}supplier-base-report.php`,
        {}, // empty body
        {
          params: { supplier_id: supplierId },
          headers: { "Content-Type": "application/json" },
        }
      )
      .then((res) => {
        const response = res.data;
        if (response.status) {
          setRawData(response.data || []); // store all data
          setSummary(response.summary || null);
        } else {
          setError("Failed to fetch data");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch data");
        setLoading(false);
      });
  }, [supplierId]);

  // Filter data by startDate, endDate, invoiceNo, and invoiceSearch (frontend only)
  const filteredData = rawData.filter((row) => {
    if (!row.purchase_date) return false;
    const rowDate = row.purchase_date.slice(0, 10); // 'YYYY-MM-DD'
    if (startDate && rowDate < startDate) return false;
    if (endDate && rowDate > endDate) return false;
    if (invoiceNo && row.invoice_no !== invoiceNo) return false;
    if (
      invoiceSearch &&
      row.invoice_no &&
      !row.invoice_no.toLowerCase().includes(invoiceSearch.toLowerCase())
    )
      return false;
    return true;
  });

  // Calculate filtered summary
  const filteredSummary = filteredData.reduce(
    (acc, row) => {
      acc.total_invoice_amount += Number(row.total_amt) || 0;
      acc.total_paid_amount += Number(row.paid_amount) || 0;
      acc.total_due_amount += Number(row.due_amount) || 0;
      return acc;
    },
    { total_invoice_amount: 0, total_paid_amount: 0, total_due_amount: 0 }
  );

  // Pagination
  const totalRows = filteredData.length;
  const totalPages =
    rowsPerPage === "all" ? 1 : Math.ceil(totalRows / rowsPerPage);

  // Determine if filtering by invoice
  const isInvoiceFiltered = !!invoiceNo;

  // Show all rows if filtering by invoice, or if rowsPerPage is 'all', else paginate
  const paginatedRows = isInvoiceFiltered
    ? filteredData
    : rowsPerPage === "all"
    ? filteredData
    : filteredData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      );

  // For export: by default export all filtered data, but when filtering by invoice, export only those rows
  const exportRows = isInvoiceFiltered ? filteredData : filteredData;

  // Find the last index for each invoice_no in paginatedRows
  const lastRowIndexByInvoice = {};
  paginatedRows.forEach((row, idx) => {
    if (row.invoice_no) lastRowIndexByInvoice[row.invoice_no] = idx;
  });

  // Calculate totals for the correct set (unique invoices only)
  const invoiceSeen = {};
  let uniqueTotalAmt = 0,
    uniquePaidAmt = 0,
    uniqueDueAmt = 0;
  paginatedRows.forEach((row) => {
    if (row.invoice_no && !invoiceSeen[row.invoice_no]) {
      uniqueTotalAmt += Number(row.total_amt) || 0;
      uniquePaidAmt += Number(row.total_paid) || 0; // Use total_paid for paid amount
      uniqueDueAmt += Number(row.due_amount) || 0;
      invoiceSeen[row.invoice_no] = true;
    }
  });

  // Remove invoiceTotals logic, just use row.total_amt, row.paid_amount, row.due_amount directly

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  function handleExportTable() {
    const headers = [
      "Invoice No",
      "Date",
      "Product Name",
      "Unit (Price)",
      "Quantity",
      "Total Amount",
      "Paid Amount",
      "Due Amount",
      "Payment Type",
    ];
    const rows = exportRows.map((row) => [
      row.invoice_no,
      row.payment_date ? row.payment_date.slice(0, 10) : "",
      row.product_name,
      `${row.per_unit_price} ${row.unit_name}`,
      row.quantity,
      row.total_amt,
      row.paid_amount,
      row.due_amount,
      row.payment_type || "-",
    ]);
    // Calculate totals for exportRows
    const exportTotals = exportRows.reduce(
      (acc, row) => {
        acc.total_amt += Number(row.total_amt) || 0;
        acc.paid_amount += Number(row.paid_amount) || 0;
        acc.due_amount += Number(row.due_amount) || 0;
        return acc;
      },
      { total_amt: 0, paid_amount: 0, due_amount: 0 }
    );
    // Add summary row using exportTotals
    if (exportRows.length > 0) {
      rows.push([
        "",
        "",
        "",
        "Total",
        exportTotals.total_amt || "",
        exportTotals.paid_amount || "",
        exportTotals.due_amount || "",
        "",
      ]);
    }
    let csvContent = "";
    csvContent += headers.join(",") + "\n";
    rows.forEach((r) => {
      csvContent +=
        r
          .map(
            (val) =>
              '"' +
              (val !== undefined ? String(val).replace(/"/g, '""') : "") +
              '"'
          )
          .join(",") + "\n";
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "supplier_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handleExportExcelXML() {
    const headers = [
      "Date",
      "Invoice No",
      "Product Name",
      "Qty x Unit",
      "Total Amount",
      "Paid Amount",
      "Due Amount",
      "Payment Type",
    ];
    const rows = exportRows.map((row) => [
      row.payment_date ? row.payment_date.slice(0, 10) : "",
      row.invoice_no || "",
      row.product_name || "",
      row.quantity && row.per_unit_price && row.unit_name
        ? `${row.quantity} x ${row.per_unit_price}${row.unit_name}`
        : "-",
      row.total_amt || "",
      row.paid_amount || "",
      row.due_amount || "",
      row.payment_type || "-",
    ]);
    // Calculate totals for exportRows
    const exportTotals = exportRows.reduce(
      (acc, row) => {
        acc.total_amt += Number(row.total_amt) || 0;
        acc.paid_amount += Number(row.paid_amount) || 0;
        acc.due_amount += Number(row.due_amount) || 0;
        return acc;
      },
      { total_amt: 0, paid_amount: 0, due_amount: 0 }
    );
    // Add summary row using exportTotals
    if (exportRows.length > 0) {
      rows.push([
        "",
        "",
        "",
        "Total",
        exportTotals.total_amt || "",
        exportTotals.paid_amount || "",
        exportTotals.due_amount || "",
        "",
      ]);
    }
    let xml = `<?xml version="1.0"?>\n<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n xmlns:o="urn:schemas-microsoft-com:office:office"\n xmlns:x="urn:schemas-microsoft-com:office:excel"\n xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n <Worksheet ss:Name="Sheet1">\n  <Table>\n   <Row>\n     ${headers
      .map((h) => `<Cell><Data ss:Type="String">${h}</Data></Cell>`)
      .join("")}\n   </Row>\n   ${rows
      .map(
        (r) =>
          `<Row>${r
            .map((val) => `<Cell><Data ss:Type="String">${val}</Data></Cell>`)
            .join("")}</Row>`
      )
      .join("")}\n  </Table>\n </Worksheet>\n</Workbook>`;

    const blob = new Blob([xml], { type: "application/vnd.ms-excel" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "supplier_report.xml");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handleExportXLS() {
    const headers = [
      "Date",
      "Invoice No",
      "Product Name",
      "Qty x Unit",
      "Total Amount",
      "Paid Amount",
      "Due Amount",
      "Payment Type",
    ];
    const rows = exportRows.map((row) => [
      row.purchase_date ? row.purchase_date : "",
      row.invoice_no || "",
      row.product_name || "",
      row.quantity && row.per_unit_price && row.unit_name
        ? `${row.quantity} x ${row.per_unit_price}${row.unit_name}`
        : "-",
      row.total_amt || "",
      row.paid_amount || "",
      row.due_amount || "",
      row.payment_type || "-",
    ]);
    // Calculate totals for exportRows
    const exportTotals = exportRows.reduce(
      (acc, row) => {
        acc.total_amt += Number(row.total_amt) || 0;
        acc.paid_amount += Number(row.paid_amount) || 0;
        acc.due_amount += Number(row.due_amount) || 0;
        return acc;
      },
      { total_amt: 0, paid_amount: 0, due_amount: 0 }
    );
    // Add summary row using exportTotals
    if (exportRows.length > 0) {
      rows.push([
        "",
        "",
        "",
        "Total",
        exportTotals.total_amt || "",
        exportTotals.paid_amount || "",
        exportTotals.due_amount || "",
        "",
      ]);
    }
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "supplier_report.xls", { bookType: "xls" });
  }

  return (
    <div className="reports-content">
      <div className="page-header">
        <div className="header-content">
          <h1>
            Supplier Report
            {supplierName && (
              <span
                style={{
                  fontSize: "1.1em",
                  fontWeight: "bold",
                  marginLeft: 10,
                }}
              >
                - {supplierName}
              </span>
            )}
          </h1>
          <p>Filter and view supplier transactions</p>
        </div>
      </div>
      <div className="filters-section">
        <label>
          Start Date:
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
        <label>
          End Date:
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </label>
        <div className="d-block" style={{ marginTop: "10px" }}>
          <label>Invoice No:</label>
          <select
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
          >
            <option value="">All</option>
            {Array.from(
              new Set(rawData.map((row) => row.invoice_no).filter(Boolean))
            ).map((inv) => (
              <option key={inv} value={inv}>
                {inv}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Invoice Summary Section */}
      {summary &&
        Array.isArray(summary.invoice_summary) &&
        summary.invoice_summary.length > 0 && (
          <div className="invoice-summary-section" style={{ margin: "20px 0" }}>
            <h3 style={{ marginBottom: "10px" }}>Invoice Summary</h3>
            <table className="report-table" style={{ marginBottom: "0" }}>
              <thead>
                <tr>
                  <th>Invoice No</th>
                  <th>Total Amount</th>
                  <th>Paid Amount</th>
                  <th>Due Amount</th>
                </tr>
              </thead>
              <tbody>
                {summary.invoice_summary.map((inv) => (
                  <tr key={inv.invoice_no}>
                    <td>{inv.invoice_no}</td>
                    <td>₹ {inv.total_amount}</td>
                    <td>₹ {inv.paid_amount}</td>
                    <td>₹ {inv.due_amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {/* Summary Section as Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-label">Total Amount</div>
          <div className="summary-value">
            ₹ {summary ? summary.total_amt : 0}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Paid Amount</div>
          <div className="summary-value">
            ₹ {summary ? summary.paid_amount : 0}
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-label">Due Amount</div>
          <div className="summary-value">
            ₹ {summary ? summary.due_amount : 0}
          </div>
        </div>
      </div>
      {/* Table Section */}
      <div className="table-section">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "10px",
            gap: "10px",
          }}
        >
          {/* Left side: Rows per page and search field grouped */}
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <label className="rows-per-page-label">
              Rows per page:
              <select
                className="rows-per-page-select"
                value={rowsPerPage}
                onChange={(e) =>
                  setRowsPerPage(
                    e.target.value === "all" ? "all" : Number(e.target.value)
                  )
                }
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value="all">All</option>
              </select>
            </label>
          </div>
          {/* Right side: Export button */}
          <button className="export-btn" onClick={handleExportXLS}>
            Export as Excel
          </button>
        </div>
        <div className="table-card">
          {loading ? (
            <div>Loading...</div>
          ) : error ? (
            <div style={{ color: "red" }}>{error}</div>
          ) : (
            <table className="report-table">
              <thead>
                <tr>
                  <th> Date</th>
                  <th>Invoice No</th>
                  <th>Product Name</th>
                  <th>Qty x Unit</th>
                  <th>Total Amount</th>
                  <th>Paid Amount</th>
                  <th>Due Amount</th>
                  <th>Payment Type</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan="10">No data found</td>
                  </tr>
                ) : (
                  <>
                    {paginatedRows.map((row, idx) => (
                      <tr key={row.payment_id || idx}>
                        <td>{row.purchase_date}</td>
                        <td>{row.invoice_no}</td>
                        <td>{row.product_name}</td>
                        <td>
                          {row.quantity && row.per_unit_price && row.unit_name
                            ? `${row.quantity} x ${row.per_unit_price}${row.unit_name}`
                            : "-"}
                        </td>
                        <td>₹ {row.total_amt}</td>
                        <td>₹ {row.paid_amount}</td>
                        <td
                          className={
                            row.invoice_no &&
                            lastRowIndexByInvoice[row.invoice_no] === idx
                              ? "highlight-due"
                              : ""
                          }
                        >
                          ₹ {row.due_amount}
                        </td>
                        <td>{row.payment_type || "-"}</td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: "bold", background: "#f5f5f5" }}>
                      <td
                        className="total-row"
                        colSpan={4}
                        style={{ textAlign: "right" }}
                      >
                        Total:
                      </td>
                      <td className="total-row">₹ {uniqueTotalAmt}</td>
                      <td className="total-row">₹ {uniquePaidAmt}</td>
                      <td className="total-row">₹ {uniqueDueAmt}</td>
                      <td className="total-row"></td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {/* Pagination Controls */}
      {!isInvoiceFiltered && rowsPerPage !== "all" && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
